import { useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'

export interface UseBlockNavigationArgs {
  /**
   * While true, any attempt to leave the current route is cancelled and
   * `onAttempt` is invoked instead. Flip to false to release the guard.
   */
  when: boolean
  /**
   * Called instead of navigating, whenever a blocked navigation is attempted.
   * Typically opens a confirmation modal.
   */
  onAttempt: () => void
  /**
   * When true, also register a `beforeunload` handler so tab-close / hard
   * refresh shows the browser's native "Leave site?" dialog. The message
   * cannot be customized — browsers always show their own generic copy.
   */
  warnOnUnload?: boolean
}

/**
 * Blocks navigation away from the current route — both in-app (router `push`
 * / `goBack`) and the browser Back/Forward buttons.
 *
 * Uses `history.block` from the history v4 instance behind React Router v5.
 * For POP transitions (browser Back) the browser has already changed the URL
 * by the time the block callback runs; history's internal `revertPop` pushes
 * the entry back, so the guard holds. That is the same mechanism React
 * Router's `<Prompt>` relies on.
 *
 * Limitation: `beforeunload` (tab close, hard refresh, external navigation)
 * cannot show a custom dialog — hence `warnOnUnload` only opts into the
 * browser's own generic prompt. Callers that need correctness beyond the
 * guard should also redirect defensively on load.
 */
export function useBlockNavigation({
  when,
  onAttempt,
  warnOnUnload = false,
}: UseBlockNavigationArgs): void {
  const history = useHistory()

  // Keep the latest callback in a ref so toggling `when` is what re-registers
  // the block — not every render that passes a new inline function.
  const onAttemptRef = useRef(onAttempt)
  useEffect(() => {
    onAttemptRef.current = onAttempt
  }, [onAttempt])

  useEffect(() => {
    if (!when) {
      return
    }

    const unblock = history.block(() => {
      onAttemptRef.current()
      // Returning false cancels the transition. history reverts the URL for
      // POP transitions that the browser already applied.
      return false
    })

    return unblock
  }, [when, history])

  useEffect(() => {
    if (!when || !warnOnUnload) {
      return
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent): string => {
      event.preventDefault()
      // Assigning returnValue is what actually triggers the native dialog;
      // the string itself is ignored by modern browsers.
      event.returnValue = ''
      return ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [when, warnOnUnload])
}
